import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDatabase } from '../context/DatabaseContext';
import { useToast } from '../context/ToastContext';
import { getSkills, deleteSkill, discoverSkills, getSkillFolders, createSkillFolder, deleteSkillFolder } from '../services/skillApi';
import {
    ArrowLeft,
    Plus,
    Edit2,
    Trash2,
    Loader,
    RefreshCw,
    FileText,
    Folder,
    ChevronRight,
    FolderPlus,
    X
} from 'lucide-react';
import { ConfirmModal } from '../components/ui/ConfirmModal';

/**
 * Root: list subfolders (add empty folders), open a folder to manage skills.
 * Inside ``/folder/:folderSlug``: list skills + create skill only there.
 */
export const SkillsView = () => {
    const { t } = useTranslation('pages-misc-5');
    const navigate = useNavigate();
    const { databaseId, folderSlug: folderSlugParam } = useParams();
    const { databases } = useDatabase();
    const { showToast } = useToast();

    const activeFolder = folderSlugParam || null;

    const [skills, setSkills] = useState([]);
    const [foldersFromApi, setFoldersFromApi] = useState([]);
    const [loading, setLoading] = useState(false);
    const [foldersLoading, setFoldersLoading] = useState(false);
    const [currentDatabase, setCurrentDatabase] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [skillToDelete, setSkillToDelete] = useState(null);
    const [addFolderModalOpen, setAddFolderModalOpen] = useState(false);
    const [modalFolderName, setModalFolderName] = useState('');
    const [creatingFolder, setCreatingFolder] = useState(false);
    const [deleteFolderSlug, setDeleteFolderSlug] = useState(null);
    const [deletingFolder, setDeletingFolder] = useState(false);

    const fetchSkills = useCallback(async () => {
        if (!databaseId) return;

        setLoading(true);
        try {
            const response = await getSkills(databaseId);
            const skillsData = response.data?.skills || [];
            setSkills(skillsData);
        } catch (error) {
            console.error('Failed to fetch skills:', error);
            showToast(t('skillsView.failedToLoadSkills'), 'error');
        } finally {
            setLoading(false);
        }
    }, [databaseId, showToast, t]);

    const fetchFolders = useCallback(async () => {
        if (!databaseId || activeFolder) return;
        setFoldersLoading(true);
        try {
            const response = await getSkillFolders(databaseId);
            setFoldersFromApi(response.data?.folders || []);
        } catch (error) {
            console.error('Failed to fetch skill folders:', error);
            showToast(t('skillsView.failedToLoadFolders'), 'error');
        } finally {
            setFoldersLoading(false);
        }
    }, [databaseId, activeFolder, showToast, t]);

    useEffect(() => {
        if (databaseId) {
            const db = databases.find((d) => d.id === parseInt(databaseId, 10));
            if (db) setCurrentDatabase(db);
        }
        fetchSkills();
    }, [databaseId, databases, fetchSkills]);

    useEffect(() => {
        fetchFolders();
    }, [fetchFolders]);

    const groupedSkills = useMemo(() => {
        const buckets = {};
        skills.forEach((s) => {
            const key = s.folder || '';
            if (!buckets[key]) buckets[key] = [];
            buckets[key].push(s);
        });
        const keys = Object.keys(buckets).sort((a, b) => {
            if (a === '') return 1;
            if (b === '') return -1;
            return a.localeCompare(b);
        });
        return keys.map((folder) => ({
            folder,
            items: buckets[folder].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        }));
    }, [skills]);

    const folderSlugSet = useMemo(() => {
        const set = new Set();
        foldersFromApi.forEach((f) => {
            if (f.slug) set.add(f.slug);
        });
        groupedSkills.forEach(({ folder }) => {
            if (folder) set.add(folder);
        });
        return set;
    }, [foldersFromApi, groupedSkills]);

    const sortedFolderSlugs = useMemo(() => [...folderSlugSet].sort((a, b) => a.localeCompare(b)), [folderSlugSet]);

    const rootSkills = useMemo(() => {
        const g = groupedSkills.find((x) => x.folder === '');
        return g ? g.items : [];
    }, [groupedSkills]);

    const folderSkills = useMemo(() => {
        if (!activeFolder) return [];
        const g = groupedSkills.find((x) => x.folder === activeFolder);
        return g ? g.items : [];
    }, [groupedSkills, activeFolder]);

    const handleBack = () => {
        if (activeFolder) {
            navigate(`/skills/${databaseId}`);
            return;
        }
        navigate('/skills');
    };

    const dbForNav = useMemo(() => {
        if (currentDatabase) return currentDatabase;
        const id = parseInt(databaseId, 10);
        if (!Number.isFinite(id)) return null;
        return { id, name: t('skillsView.databaseFallback', { id }) };
    }, [currentDatabase, databaseId, t]);

    const folderPath = (slug) => `/skills/${databaseId}/folder/${encodeURIComponent(slug)}`;

    const handleOpenFolder = (slug) => {
        navigate(folderPath(slug));
    };

    const openAddFolderModal = () => {
        setModalFolderName('');
        setAddFolderModalOpen(true);
    };

    const closeAddFolderModal = () => {
        if (creatingFolder) return;
        setAddFolderModalOpen(false);
        setModalFolderName('');
    };

    const submitNewFolder = async () => {
        const raw = modalFolderName.trim();
        if (!raw) {
            showToast(t('skillsView.enterFolderName'), 'error');
            return;
        }
        if (!dbForNav?.id) {
            showToast(t('skillsView.databaseNotLoaded'), 'error');
            return;
        }
        setCreatingFolder(true);
        try {
            const resp = await createSkillFolder({ database_id: dbForNav.id, folder: raw });
            const createdKey = resp.data?.folder;
            showToast(resp.data?.message === 'Folder already exists' ? t('skillsView.openedFolder') : t('skillsView.subfolderCreated'), 'success');
            setAddFolderModalOpen(false);
            setModalFolderName('');
            await fetchFolders();
            if (createdKey) {
                navigate(folderPath(createdKey));
            }
        } catch (e) {
            console.error(e);
            showToast(t('skillsView.failedToCreateSubfolder'), 'error');
        } finally {
            setCreatingFolder(false);
        }
    };

    const handleCreateSkill = () => {
        if (!activeFolder || !dbForNav) {
            showToast(t('skillsView.openSubfolderFirst'), 'error');
            return;
        }
        navigate(`/skills/${databaseId}/folder/${encodeURIComponent(activeFolder)}/create`, {
            state: { database: dbForNav }
        });
    };

    const handleOpenSkill = (skill) => {
        navigate(`/skills/${databaseId}/edit/${skill.id}`, {
            state: {
                database: currentDatabase || dbForNav,
                skill,
                returnFolder: activeFolder || null
            }
        });
    };

    const handleDeleteFolderConfirm = async () => {
        if (!deleteFolderSlug || !databaseId) return;
        setDeletingFolder(true);
        try {
            const resp = await deleteSkillFolder(databaseId, deleteFolderSlug);
            const n = resp.data?.deleted_skill_rows ?? 0;
            showToast(resp.data?.message || t('skillsView.deletedFolderAndSkills', { count: n }), 'success');
            setDeleteFolderSlug(null);
            await fetchSkills();
            await fetchFolders();
        } catch (error) {
            console.error('Failed to delete folder:', error);
            showToast(t('skillsView.failedToDeleteFolder'), 'error');
        } finally {
            setDeletingFolder(false);
        }
    };

    const folderDbSkillCount = (slug) => {
        const g = groupedSkills.find((x) => x.folder === slug);
        return g ? g.items.length : 0;
    };

    const handleDeleteClick = (skill) => {
        setSkillToDelete(skill);
        setDeleteModalOpen(true);
    };

    const handleDiscoverSkills = async () => {
        try {
            const response = await discoverSkills(databaseId);
            showToast(response.data?.message || t('skillsView.skillsDiscovered'), 'success');
            fetchSkills();
            if (!activeFolder) fetchFolders();
        } catch (error) {
            console.error('Failed to discover skills:', error);
            showToast(t('skillsView.failedToDiscoverSkills'), 'error');
        }
    };

    const handleDeleteConfirm = async () => {
        if (!skillToDelete) return;

        try {
            await deleteSkill(skillToDelete.id);
            showToast(t('skillsView.skillDeleted'), 'success');
            fetchSkills();
        } catch (error) {
            console.error('Failed to delete skill:', error);
            showToast(t('skillsView.failedToDeleteSkill'), 'error');
        } finally {
            setDeleteModalOpen(false);
            setSkillToDelete(null);
        }
    };

    const diskPath = (skill) => {
        const fk = skill.file_key || skill.slug;
        if (!fk) return 'skills/?.md';
        return `skills/${fk}.md`;
    };

    const folderMarkdownCount = (slug) => {
        const fromApi = foldersFromApi.find((f) => f.slug === slug);
        if (fromApi && typeof fromApi.markdown_count === 'number') return fromApi.markdown_count;
        const g = groupedSkills.find((x) => x.folder === slug);
        return g ? g.items.length : 0;
    };

    const renderSkillCard = (skill) => (
        <div
            key={skill.id}
            onClick={() => handleOpenSkill(skill)}
            className="border border-gray-200 rounded-xl p-5 bg-white hover:border-primary-300 hover:shadow-md transition-all flex flex-col cursor-pointer"
        >
            <div className="flex items-start justify-between mb-2 gap-2">
                <h3 className="font-semibold text-gray-900 pr-2 leading-snug">{skill.name}</h3>
                <div className="flex items-center space-x-1 flex-shrink-0">
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpenSkill(skill);
                        }}
                        className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors"
                        title={t('skillsView.editMarkdown')}
                    >
                        <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(skill);
                        }}
                        className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                        title={t('skillsView.deleteTooltip')}
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>
            <p className="text-xs font-mono text-primary-700/90 mb-2 break-all">{diskPath(skill)}</p>
            <p className="text-sm text-gray-500 mb-4 line-clamp-3 flex-1">{skill.description || t('skillsView.noDescription')}</p>
            <div className="flex items-center justify-between gap-2 mt-auto pt-2 border-t border-gray-100">
                <span className="text-xs text-gray-500 flex items-center">
                    <FileText className="w-3 h-3 mr-1 flex-shrink-0" />
                    {skill.has_file ? t('skillsView.onDisk') : t('skillsView.missingFile')}
                </span>
                <div className="flex items-center gap-2">
                    {skill.model && (
                        <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">{skill.model}</span>
                    )}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpenSkill(skill);
                        }}
                        className="px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700"
                    >
                        {t('skillsView.open')}
                    </button>
                </div>
            </div>
        </div>
    );

    const dbTitle = currentDatabase?.name || t('skillsView.databaseFallback', { id: databaseId });
    const title = activeFolder ? activeFolder : dbTitle;
    const subtitle = activeFolder
        ? t('skillsView.subtitleFolder')
        : t('skillsView.subtitleRoot');

    return (
        <div className="page-content">
            <div className="mb-8">
                <button
                    onClick={handleBack}
                    className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t('skillsView.back')}
                </button>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="page-title">{title}</h1>
                        <p className="page-subtitle">{subtitle}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            type="button"
                            onClick={handleDiscoverSkills}
                            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm transition-colors flex items-center space-x-2 shadow-sm"
                        >
                            <RefreshCw className="w-4 h-4" />
                            <span>{t('skillsView.discoverSkills')}</span>
                        </button>
                        {!activeFolder && (
                            <button
                                type="button"
                                onClick={openAddFolderModal}
                                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm transition-colors flex items-center space-x-2 shadow-sm"
                            >
                                <FolderPlus className="w-4 h-4" />
                                <span>{t('skillsView.addSubfolder')}</span>
                            </button>
                        )}
                        {activeFolder && (
                            <button
                                onClick={handleCreateSkill}
                                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium text-sm transition-colors flex items-center space-x-2"
                            >
                                <Plus className="w-4 h-4" />
                                <span>{t('skillsView.createSkill')}</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                {activeFolder ? (
                    <>
                        <div className="flex items-center justify-between mb-6">
                            <p className="text-sm text-gray-600">
                                <span className="font-medium text-gray-900">{folderSkills.length}</span>{' '}
                                {t('skillsView.markdownFilesInFolder', { count: folderSkills.length })}
                            </p>
                        </div>
                        {loading ? (
                            <div className="flex justify-center py-12">
                                <Loader className="w-8 h-8 animate-spin text-primary-600" />
                            </div>
                        ) : folderSkills.length === 0 ? (
                            <div className="text-left py-12">
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('skillsView.noSkillsInFolder')}</h3>
                                <p className="text-gray-600 mb-6 max-w-lg">
                                    {t('skillsView.createOrDiscoverPrefix')} <strong>{t('skillsView.createSkill')}</strong>{' '}
                                    {t('skillsView.createOrDiscoverMiddle')} <strong>{t('skillsView.discoverSkills')}</strong>{' '}
                                    {t('skillsView.createOrDiscoverSuffix')}
                                </p>
                                <button
                                    onClick={handleCreateSkill}
                                    className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition-colors inline-flex items-center space-x-2"
                                >
                                    <Plus className="w-5 h-5" />
                                    <span>{t('skillsView.createSkill')}</span>
                                </button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {folderSkills.map(renderSkillCard)}
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        {foldersLoading ? (
                            <div className="flex justify-center py-8">
                                <Loader className="w-8 h-8 animate-spin text-primary-600" />
                            </div>
                        ) : sortedFolderSlugs.length === 0 ? (
                            <p className="text-gray-600 text-sm py-4">
                                {t('skillsView.noFoldersYetPrefix')} <strong>{t('skillsView.addSubfolder')}</strong>{' '}
                                {t('skillsView.noFoldersYetSuffix')}
                            </p>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
                                {sortedFolderSlugs.map((slug) => (
                                    <div
                                        key={slug}
                                        className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50/40 hover:border-primary-300 hover:shadow-md transition-all flex flex-row items-stretch min-h-[5.5rem]"
                                    >
                                        <button
                                            type="button"
                                            onClick={() => handleOpenFolder(slug)}
                                            className="skills-folder-card-btn skills-folder-card-btn--open flex-1 flex items-center gap-3 p-5 text-left hover:bg-white transition-colors min-w-0"
                                        >
                                            <Folder className="w-8 h-8 text-primary-600 flex-shrink-0" />
                                            <div className="min-w-0 flex-1">
                                                <div className="font-semibold text-gray-900 truncate">{slug}</div>
                                                <div className="text-sm text-gray-500">
                                                    {t('skillsView.markdownFileCount', { count: folderMarkdownCount(slug) })}
                                                </div>
                                            </div>
                                            <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                        </button>
                                        <button
                                            type="button"
                                            title={t('skillsView.deleteFolderAndSkillsTooltip')}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setDeleteFolderSlug(slug);
                                            }}
                                            className="skills-folder-card-btn skills-folder-card-btn--delete px-3 flex-shrink-0 border-gray-200 hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors flex items-center"
                                        >
                                            <Trash2 className="w-5 h-5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {rootSkills.length > 0 && (
                            <section className="border-t border-gray-200 pt-8">
                                <h3 className="text-md font-semibold text-gray-900 mb-2">{t('skillsView.rootLegacy')}</h3>
                                <p className="text-sm text-gray-600 mb-4">
                                    {t('skillsView.rootLegacyDesc')}
                                </p>
                                {loading ? (
                                    <div className="flex justify-center py-8">
                                        <Loader className="w-6 h-6 animate-spin text-primary-600" />
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {rootSkills.map(renderSkillCard)}
                                    </div>
                                )}
                            </section>
                        )}
                    </>
                )}
            </div>

            <ConfirmModal
                isOpen={Boolean(deleteFolderSlug)}
                title={t('skillsView.deleteFolderTitle')}
                message={
                    <div>
                        <p className="mb-2">
                            {t('skillsView.deleteFolderConfirmPrefix')} <strong>{deleteFolderSlug}</strong>{' '}
                            {t('skillsView.deleteFolderConfirmSuffix')}
                        </p>
                        <p className="text-sm text-gray-600">
                            {t('skillsView.removes')} <strong>{folderMarkdownCount(deleteFolderSlug)}</strong>{' '}
                            {t('skillsView.markdownFileUnit', { count: folderMarkdownCount(deleteFolderSlug) })}{' '}
                            {t('skillsView.fromDiskAnd')}{' '}
                            <strong>{folderDbSkillCount(deleteFolderSlug)}</strong>{' '}
                            {t('skillsView.skillRecordUnit', { count: folderDbSkillCount(deleteFolderSlug) })}{' '}
                            {t('skillsView.fromDatabaseLegacyNote')}
                        </p>
                    </div>
                }
                confirmText={t('skillsView.deleteFolderButton')}
                loading={deletingFolder}
                onClose={() => {
                    if (!deletingFolder) setDeleteFolderSlug(null);
                }}
                onConfirm={handleDeleteFolderConfirm}
            />

            <ConfirmModal
                isOpen={deleteModalOpen}
                title={t('skillsView.deleteSkillTitle')}
                message={t('skillsView.deleteSkillConfirm', { name: skillToDelete?.name })}
                confirmText={t('skillsView.deleteButton')}
                onClose={() => {
                    setDeleteModalOpen(false);
                    setSkillToDelete(null);
                }}
                onConfirm={handleDeleteConfirm}
            />

            {addFolderModalOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                    onClick={creatingFolder ? undefined : closeAddFolderModal}
                >
                    <div
                        className="bg-white rounded-lg max-w-md w-full shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-6">
                            <div className="flex items-start justify-between gap-3 mb-4">
                                <h3 className="text-lg font-bold text-gray-900">New subfolder</h3>
                                {!creatingFolder && (
                                    <button
                                        type="button"
                                        onClick={closeAddFolderModal}
                                        className="text-gray-400 hover:text-gray-600"
                                        aria-label="Close"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                )}
                            </div>
                            <p className="text-sm text-gray-600 mb-3">
                                Name appears as the folder label (e.g. <strong>sales</strong>). You will open that
                                folder when it is created.
                            </p>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Folder name</label>
                            <input
                                type="text"
                                value={modalFolderName}
                                onChange={(e) => setModalFolderName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !creatingFolder) {
                                        e.preventDefault();
                                        submitNewFolder();
                                    }
                                }}
                                placeholder="e.g. sales, driver"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 mb-6"
                                disabled={creatingFolder}
                                autoFocus
                            />
                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={closeAddFolderModal}
                                    disabled={creatingFolder}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={submitNewFolder}
                                    disabled={creatingFolder}
                                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium text-sm disabled:opacity-50 flex items-center gap-2"
                                >
                                    {creatingFolder && (
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    )}
                                    Create & open
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
